import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiArchive, FiLock } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  getOfferings, createOffering, updateOffering, cancelOffering, purgeOffering,
  reviewOfferingDeleteRequest,
  getDonationCategories,
  getDerasarPersonUsers, createReceipt, getEvents,
  getEventDonations, registerSpecialDonation,
} from '../../services/api';
import { today } from '../../utils/dateUtils';
import { toDerasarPersonOptions } from '../../utils/derasarPersonOptions';
import { PageHeader, CancelConfirmModal } from '../../components/common';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { TOAST, ACTIONS, NAV, ENTITIES } from '../../constants/labels';
import { OFFERING_LABEL, EMPTY_FORM, newItem } from './offeringsConfig';
import { getPreferredPostingFinancialYear, isCurrentFinancialYear } from '../../utils/financialYears';
import OfferingsSummaryCards from './OfferingsSummaryCards';
import OfferingsFilters from './OfferingsFilters';
import OfferingsTable from './OfferingsTable';
import OfferingsCancelModal from './OfferingsCancelModal';
import OfferingsForm from './OfferingsForm';
import MultiOfferingsPage from './MultiOfferingsPage';
import OfferingsReceiptModal from './OfferingsReceiptModal';
import styles from './OfferingsPage.module.css';

const OfferingsPage = () => {
  const {
    activeDerasarId,
    activeDerasar,
    isDerasarAdmin,
    visibleFinancialYears,
    openingFinancialYear,
    activeFinancialYearId,
    setActiveFinancialYearId,
    can,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const autoOpenConsumed = useRef(false);

  const [view, setView] = useState('list'); // 'list' | 'form' | 'edit'
  const [activeSection, setActiveSection] = useState('active'); // 'active' | 'cancelled'
  const [editingOffering, setEditingOffering] = useState(null); // offering being edited

  const [offerings, setOfferings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [years, setYears] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventDonations, setEventDonations] = useState([]);
  const [personUsers, setPersonUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  /* filters */
  const [filterYear, setFilterYear] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterMember, setFilterMember] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  /* form */
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [isDateLocked, setIsDateLocked] = useState(false);
  const [hideSpecialCategories, setHideSpecialCategories] = useState(false);
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  /* receipt modal after create+receipt */
  const [receiptModal, setReceiptModal] = useState(null); // null | { memberName, offerings, totalAmount, derasarId, financialYearId }
  const [receiptSaving, setReceiptSaving] = useState(false);

  const getValidatedItems = useCallback((items, { editMode = false } = {}) => {
    const startIndex = editMode ? 1 : 0;
    const validItems = [];

    for (let index = startIndex; index < items.length; index += 1) {
      const item = items[index];
      const hasCategory = !!item.donationCategoryId;
      const amount = parseFloat(item.amount) || 0;
      const hasAmount = item.amount !== '' && item.amount !== null && item.amount !== undefined;

      if (!hasCategory && !hasAmount) continue;
      if (!hasCategory) {
        return { error: `Select a donation category for item ${index + 1}` };
      }
      if (amount <= 0) {
        return { error: `Enter an amount greater than zero for item ${index + 1}` };
      }

      validItems.push(item);
    }

    return { validItems };
  }, []);

  const getFinancialYearBounds = useCallback((financialYearId) => {
    const fy = years.find((item) => String(item.id) === String(financialYearId));
    if (!fy) return { fy: null, start: '', end: '' };

    return {
      fy,
      start: (fy.startDate || '').slice(0, 10),
      end: (fy.endDate || '').slice(0, 10),
    };
  }, [years]);

  const getDefaultOfferingDateForYear = useCallback((financialYear) => {
    if (!financialYear) return today();

    const start = (financialYear.startDate || '').slice(0, 10);
    const end = (financialYear.endDate || '').slice(0, 10);
    const todayValue = today();

    if (isCurrentFinancialYear(financialYear)) {
      if (start && todayValue < start) return start;
      if (end && todayValue > end) return end;
      return todayValue;
    }

    return start || todayValue;
  }, []);

  /* -- load data ---------------------------------------------------------- */
  const loadMeta = useCallback(async () => {
    if (!activeDerasarId) return;
    try {
      const [catRes, uRes, eventsRes] = await Promise.all([
        getDonationCategories(activeDerasarId),
        getDerasarPersonUsers(activeDerasarId),
        getEvents(activeDerasarId),
      ]);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      const users = Array.isArray(uRes.data) ? uRes.data : [];
      setPersonUsers(toDerasarPersonOptions(users));
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
    } catch {
      toast.error(TOAST.loadFailed(OFFERING_LABEL));
    }
  }, [activeDerasarId]);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const params = { derasarId: activeDerasarId };
      if (filterYear && filterYear !== 'opening') params.financialYearId = filterYear;
      if (filterCat) params.categoryId = filterCat;
      if (filterFromDate) params.fromDate = filterFromDate;
      if (filterToDate) params.toDate = filterToDate;
      const res = await getOfferings(params);
      setOfferings(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(TOAST.loadFailed(OFFERING_LABEL));
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId, filterYear, filterCat, filterFromDate, filterToDate]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  useEffect(() => {
    const timer = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!activeDerasarId) return;
    setYears(visibleFinancialYears);
    setFilterYear((prev) => prev || activeFinancialYearId || '');
  }, [activeDerasarId, visibleFinancialYears, activeFinancialYearId]);

  const filterYearOptions = useMemo(() => {
    if (!isDerasarAdmin || !openingFinancialYear) return years;
    const hasOpening = years.some((year) => String(year.id) === String(openingFinancialYear.id));
    return hasOpening ? years : [...years, openingFinancialYear];
  }, [isDerasarAdmin, openingFinancialYear, years]);

  const selectedFilterYear = useMemo(
    () => filterYearOptions.find((year) => String(year.id) === String(filterYear)) || null,
    [filterYearOptions, filterYear],
  );

  const filterMinDate = selectedFilterYear?.startDate?.slice(0, 10) || '';
  const filterMaxDate = selectedFilterYear?.endDate?.slice(0, 10) || '';

  useEffect(() => {
    if (!filterYear) {
      setFilterFromDate('');
      setFilterToDate('');
      return;
    }

    setFilterFromDate(filterMinDate || '');
    setFilterToDate(filterMaxDate || '');
  }, [filterYear, filterMinDate, filterMaxDate]);

  /* -- client-side filtering -------------------------------------------- */
  const applyCommonFilters = (list) => {
    let result = list;
    // "opening" year is a special client-side case — no financialYearId is sent to server
    if (filterYear === 'opening') {
      result = result.filter((p) => (p.financialYearId?.id ?? p.financialYearId) == null);
    }
    if (filterMember.trim()) {
      const q = filterMember.trim().toLowerCase();
      result = result.filter((p) => {
        const name = p.creator?.name || p.member?.name || p.userId?.name || String(p.userId || '');
        return name.toLowerCase().includes(q);
      });
    }
    return result;
  };

  const filteredActive = useMemo(() => {
    let result = offerings.filter((p) => p.status !== 'cancelled');
    result = applyCommonFilters(result);
    if (filterStatus) result = result.filter((p) => p.status === filterStatus);
    return result.sort((a, b) => (a.offeringDate || '').localeCompare(b.offeringDate || ''));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerings, filterYear, filterStatus, filterCat, filterMember, filterFromDate, filterToDate]);

  const filteredCancelled = useMemo(() => {
    return applyCommonFilters(offerings.filter((p) => p.status === 'cancelled'))
      .sort((a, b) => (a.offeringDate || '').localeCompare(b.offeringDate || ''));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerings, filterYear, filterCat, filterMember, filterFromDate, filterToDate]);

  const filteredOfferings = activeSection === 'cancelled' ? filteredCancelled : filteredActive;

  const clearFilters = () => {
    setFilterYear('');
    setFilterStatus('');
    setFilterCat('');
    setFilterMember('');
    setFilterFromDate('');
    setFilterToDate('');
  };

  /* ── derived totals — always from active offerings only ─────────────── */
  const totalOffered  = filteredActive.reduce((s, p) => s + (Number(p.offeredAmount)  || 0), 0);
  const totalPaid     = filteredActive.reduce((s, p) => s + (Number(p.paidAmount)     || 0), 0);
  const totalPending  = filteredActive.reduce((s, p) => s + (Number(p.pendingAmount)  || 0), 0);

  /* -- cancel hook ------------------------------------------------------- */
  const { cancelModal, cancelReason, cancelling, openCancel, closeCancel, setReason, handleCancel } =
    useCancelConfirm(cancelOffering, load, 'Offering updated');

  /* -- delete cancelled offering hook ------------------------------------ */
  const {
    cancelModal: deleteModal,
    cancelling: deleting,
    openCancel: openDelete,
    closeCancel: closeDelete,
    handleCancel: handleDelete,
  } = useCancelConfirm(
    (id) => purgeOffering(id),
    load,
    'Offering deleted',
    { requireReason: false },
  );

  /* -- form open / close ------------------------------------------------ */
  const openForm = (overridePledgeDate = null, lockDate = false, endRange = null, isEventDay = false) => {
    let openFy = getPreferredPostingFinancialYear(years);
    if (activeFinancialYearId) {
      openFy = years.find((year) => String(year.id) === String(activeFinancialYearId)) || openFy;
    }

    const fyId = openFy?.id || '';
    let pDate = overridePledgeDate || getDefaultOfferingDateForYear(openFy);
    
    // Event flows can further narrow the date range.
    // Normal offering entry should rely on the selected financial year's own start/end dates.
    let min = '';
    let max = '';

    if (overridePledgeDate) {
      min = overridePledgeDate;
    }
    if (endRange) {
      max = endRange;
    }

    // Ensure initial date is within any event-specific range
    if (min && pDate < min) pDate = min;
    if (max && pDate > max) pDate = max;

    setForm({
      ...EMPTY_FORM,
      financialYearId: fyId,
      offeringDate: pDate,
      isEventDay: !!isEventDay,
      items: [newItem()],
    });
    if (fyId) setActiveFinancialYearId(fyId);
    setIsDateLocked(lockDate);
    setMinDate(min);
    setMaxDate(max);
    setHideSpecialCategories(false);
    setView('form');
  };

  // Auto-open form when navigated here from EventsPage
  useEffect(() => {
    if (loading || autoOpenConsumed.current) return;
    if (!location.state?.autoOpenForm) return;
    autoOpenConsumed.current = true;
    const eventId = location.state?.eventId || null;
    openForm(
      location.state.offeringDate || null,
      location.state.lockDate || false,
      location.state.endDate || null,
      location.state.isEventDay || false
    );
    if (eventId) {
      setForm((f) => ({ ...f, eventId, isEventDay: true }));
      setHideSpecialCategories(true);
    }
    window.history.replaceState({}, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const closeForm = () => {
    setView('list');
    setForm({ ...EMPTY_FORM });
    setEditingOffering(null);
    setIsDateLocked(false);
    setMinDate('');
    setMaxDate('');
  };

  useEffect(() => {
    if ((view !== 'form' && view !== 'edit') || !form.financialYearId) return;

    const { start, end } = getFinancialYearBounds(form.financialYearId);
    const lowerBound = minDate || start;
    const upperBound = maxDate || end;
    if (!lowerBound && !upperBound) return;

    let nextDate = form.offeringDate;
    if (lowerBound && nextDate < lowerBound) nextDate = lowerBound;
    if (upperBound && nextDate > upperBound) nextDate = upperBound;

    if (nextDate !== form.offeringDate) {
      setForm((current) => ({ ...current, offeringDate: nextDate }));
    }
  }, [form.financialYearId, form.offeringDate, getFinancialYearBounds, maxDate, minDate, view]);

  useEffect(() => {
    if (!form.offeringDate || (!form.isEventDay && !form.eventId)) return;

    const matchingIds = new Set(
      events
        .filter((event) => {
          const start = (event.eventDate || '').slice(0, 10);
          const end = (event.endDate || event.eventDate || '').slice(0, 10);
          return start && end && form.offeringDate >= start && form.offeringDate <= end;
        })
        .map((e) => String(e.id)),
    );

    const currentStillMatches = form.eventId ? matchingIds.has(String(form.eventId)) : false;
    if (!currentStillMatches) {
      setForm((current) => ({ ...current, isEventDay: false, eventId: null }));
    }
  }, [events, form.isEventDay, form.eventId, form.offeringDate]);

  useEffect(() => {
    if (!form?.eventId || !activeDerasarId) {
      setEventDonations([]);
      return;
    }
    getEventDonations(form.eventId, activeDerasarId)
      .then((res) => {
        const all = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        setEventDonations(all);
      })
      .catch(() => setEventDonations([]));
  }, [form?.eventId, activeDerasarId]);

  /* -- receipt shortcut ------------------------------------------------- */
  const handleReceiptClick = (offering) => {
    const donorId = offering.userId?.id ?? offering.userId;
    if (!donorId) return;
    
    const todayVal = today();
    const offeringDate = (offering.offeringDate || '').slice(0, 10);
    
    const allowAdvancePayment = offeringDate && todayVal < offeringDate;
    
    navigate('/accounting/receipts/new', {
      state: {
        prefillDonorId: String(donorId),
        prefillOfferingId: String(offering.id),
        prefillFinancialYearId: String(offering.financialYearId?.id ?? offering.financialYearId ?? ''),
        prefillOfferingDate: offeringDate,
        allowAdvancePayment,
      },
    });
  };

  /* -- open edit -------------------------------------------------------- */
  const openEdit = (offering) => {
    const cat = categories.find((c) => {
      const id = offering.donationCategoryId?.id ?? offering.donationCategoryId;
      return String(c.id) === String(id);
    });

    const userId = offering.userId?.id ?? offering.userId;

    const storedQty  = offering.qty != null ? String(offering.qty) : '1';
    const storedRate = offering.defaultRate != null
      ? String(parseFloat(offering.defaultRate))
      : String(parseFloat(offering.offeredAmount) || 0);

    setEditingOffering(offering);
    setForm({
      ...EMPTY_FORM,
      userId,
      userName: offering.member?.name || offering.creator?.name || '',
      financialYearId: offering.financialYearId?.id ?? offering.financialYearId,
      offeringDate: (offering.offeringDate || '').slice(0, 10),
      isEventDay: !!offering.isEventDay,
      notes: offering.notes || '',
      items: [{
        ...newItem(),
        donationCategoryId: cat?.id ?? (offering.donationCategoryId?.id ?? offering.donationCategoryId),
        categoryName: cat?.name ?? (offering.DonationCategory?.name || offering.donationCategory?.name || ''),
        qty: storedQty,
        rate: storedRate,
        amount: String(parseFloat(offering.offeredAmount) || 0),
      }],
    });
    setView('edit');
  };

  /* -- submit ----------------------------------------------------------- */
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.userId)         return toast.error(TOAST.selectRequired('member'));
    if (!form.financialYearId) return toast.error(TOAST.selectRequired('financial year'));
    if (!form.offeringDate)   return toast.error(TOAST.fieldRequired('Offering date'));

    const { validItems: allValidItems, error } = getValidatedItems(form.items);
    if (error) return toast.error(error);
    const validItems = (allValidItems || []).filter((it) => !it.isSpecialDonation);
    if (validItems.length === 0)
      return toast.error('Add at least one item with a category and amount');

    setSaving(true);
    try {
      await Promise.all(
        validItems.map((it) =>
          createOffering({
            derasarId: activeDerasarId,
            userId: form.userId,
            donationCategoryId: it.donationCategoryId,
            financialYearId: form.financialYearId,
            qty: parseInt(it.qty, 10) || null,
            defaultRate: parseFloat(it.rate) || null,
            offeredAmount: parseFloat(it.amount) || 0,
            offeringDate: form.offeringDate,
            isEventDay: !!form.isEventDay,
            notes: form.notes || undefined,
            eventId: form.eventId || undefined,
          })
        )
      );
      const count = validItems.length;
      toast.success(`${count} ${count > 1 ? OFFERING_LABEL.plural : OFFERING_LABEL.singular} created`);
      closeForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || TOAST.actionFailed('create offering'));
    } finally {
      setSaving(false);
    }
  };

  /* -- Create offering(s) then open receipt modal ------------------------ */
  const handleCreateWithReceipt = async () => {
    if (!form.userId)          return toast.error(TOAST.selectRequired('member'));
    if (!form.financialYearId) return toast.error(TOAST.selectRequired('financial year'));
    if (!form.offeringDate)    return toast.error(TOAST.fieldRequired('Offering date'));

    const { validItems: allValidItemsWR, error } = getValidatedItems(form.items);
    if (error) return toast.error(error);
    const validItems = (allValidItemsWR || []).filter((it) => !it.isSpecialDonation);
    if (validItems.length === 0)
      return toast.error('Add at least one item with a category and amount');

    setSaving(true);
    try {
      const results = await Promise.all(
        validItems.map((it) =>
          createOffering({
            derasarId: activeDerasarId,
            userId: form.userId,
            donationCategoryId: it.donationCategoryId,
            financialYearId: form.financialYearId,
            qty: parseInt(it.qty, 10) || null,
            defaultRate: parseFloat(it.rate) || null,
            offeredAmount: parseFloat(it.amount) || 0,
            offeringDate: form.offeringDate,
            isEventDay: !!form.isEventDay,
            notes: form.notes || undefined,
            eventId: form.eventId || undefined,
          })
        )
      );
      const count = validItems.length;
      toast.success(`${count} ${count > 1 ? OFFERING_LABEL.plural : OFFERING_LABEL.singular} created`);

      const createdOfferings = results.map((res, i) => ({
        id: res.data?.id ?? res.data?.offering?.id,
        amount: parseFloat(validItems[i].amount) || 0,
        categoryName: validItems[i].categoryName || '',
      }));
      const totalAmount = createdOfferings.reduce((s, o) => s + o.amount, 0);

      if (!isDerasarAdmin) {
        navigate('/pay-offers', {
          state: { preselectedIds: createdOfferings.map(o => o.id) }
        });
      } else {
        setReceiptModal({
          memberName: form.userName,
          offerings: createdOfferings,
          totalAmount,
          derasarId: activeDerasarId,
          financialYearId: form.financialYearId,
          userId: form.userId,
        });
      }

      setView('list');
      setForm({ ...EMPTY_FORM });
      setIsDateLocked(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || TOAST.actionFailed('create offering'));
    } finally {
      setSaving(false);
    }
  };

  /* -- Submit receipt from modal ----------------------------------------- */
  const handleReceiptModalSubmit = async ({ receiptDate, debitLedgerAccountId, paymentMode, referenceNo, narration }) => {
    if (!receiptModal) return;
    setReceiptSaving(true);
    try {
      await createReceipt({
        derasarId: receiptModal.derasarId,
        financialYearId: receiptModal.financialYearId,
        receiptDate,
        receiptType: 'standard',
        userId: receiptModal.userId,
        debitLedgerAccountId,
        paymentMode,
        referenceNo,
        narration,
        amountInr: receiptModal.totalAmount,
        offeringSettlements: receiptModal.offerings.map((o) => ({
          offeringId: o.id,
          settledAmount: o.amount,
        })),
      });
      toast.success('Receipt created successfully');
      setReceiptModal(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create receipt');
    } finally {
      setReceiptSaving(false);
    }
  };

  /* -- update (edit) ---------------------------------------------------- */
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!form.offeringDate) return toast.error(TOAST.fieldRequired('Offering date'));
    const item = form.items[0];
    if (!item?.donationCategoryId) return toast.error('Select a donation category for item 1');
    const offeredAmount = parseFloat(item.amount) || 0;
    if (offeredAmount <= 0) return toast.error('Amount must be greater than zero');
    const { validItems: additionalItems, error } = getValidatedItems(form.items, { editMode: true });
    if (error) return toast.error(error);

    setSaving(true);
    try {
      await updateOffering(editingOffering.id, {
        qty: parseInt(item.qty, 10) || null,
        defaultRate: parseFloat(item.rate) || null,
        offeredAmount,
        offeringDate: form.offeringDate,
        isEventDay: !!form.isEventDay,
        notes: form.notes || undefined,
      });
      if (additionalItems.length > 0) {
        await Promise.all(
          additionalItems.map((it) =>
            createOffering({
              derasarId: activeDerasarId,
              userId: form.userId,
              donationCategoryId: it.donationCategoryId,
              financialYearId: form.financialYearId,
              qty: parseInt(it.qty, 10) || null,
              defaultRate: parseFloat(it.rate) || null,
              offeredAmount: parseFloat(it.amount) || 0,
              offeringDate: form.offeringDate,
              isEventDay: !!form.isEventDay,
              notes: form.notes || undefined,
              eventId: form.eventId || undefined,
            })
          )
        );
      }
      toast.success(additionalItems.length > 0
        ? `${OFFERING_LABEL.singular} updated and ${additionalItems.length} item${additionalItems.length > 1 ? 's' : ''} added`
        : `${OFFERING_LABEL.singular} updated`);
      closeForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || TOAST.actionFailed('update offering'));
    } finally {
      setSaving(false);
    }
  };

  const handleReviewDeleteRequest = async (offering, action) => {
    const confirmationMessage = action === 'approve'
      ? `Are you sure you want to approve this deletion request for OFR-${offering.id}?`
      : `Are you sure you want to reject this deletion request for OFR-${offering.id}?`;
    if (!window.confirm(confirmationMessage)) return;
    try {
      await reviewOfferingDeleteRequest(offering.id, action);
      toast.success(action === 'approve' ? 'Offering deleted' : 'Deletion request rejected');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${action} delete request`);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  /* -- multi offerings view --------------------------------------------- */
  if (view === 'multi') {
    return (
      <MultiOfferingsPage
        categories={categories}
        events={events}
        personUsers={personUsers}
        years={years}
        activeDerasarId={activeDerasarId}
        onClose={() => setView('list')}
        onSuccess={() => { setView('list'); load(); }}
      />
    );
  }

  /* -- form / edit view ------------------------------------------------- */
  if (view === 'form' || view === 'edit') {
    return (
      <OfferingsForm
        form={form}
        setForm={setForm}
        years={years}
        categories={categories}
        personUsers={personUsers}
        events={events}
        eventDonations={eventDonations}
        hideSpecialCategories={hideSpecialCategories}
        isDerasarAdmin={isDerasarAdmin}
        isOnlinePaymentEnabled={!!activeDerasar?.derasarId?.onlinePaymentEnabled}
        saving={saving}
        editMode={!!editingOffering}
        isDateLocked={isDateLocked}
        minDate={minDate}
        maxDate={maxDate}
        onClose={closeForm}
        onSubmit={view === 'edit' ? handleUpdate : handleCreate}
        onSubmitWithReceipt={view === 'form' ? handleCreateWithReceipt : undefined}
        onRegisterSpecialDonation={registerSpecialDonation}
      />
    );
  }

  /* -- list view -------------------------------------------------------- */
  return (
    <>
      <div className={styles.page}>
        <PageHeader
          icon={<FiArchive />}
          title={NAV.pledges}
          actions={
            (!isDerasarAdmin || can('offerings', 'manage')) ? (
            <>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setView('multi')}
                disabled={!!selectedFilterYear?.isClosed}
                title={selectedFilterYear?.isClosed ? 'This financial year is locked.' : undefined}
              >
                <FiPlus /> Multi {OFFERING_LABEL.plural}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => openForm()}
                disabled={!!selectedFilterYear?.isClosed}
                title={selectedFilterYear?.isClosed ? 'This financial year is locked — unlock it before adding new offerings.' : undefined}
              >
                {selectedFilterYear?.isClosed ? <FiLock /> : <FiPlus />} {ACTIONS.new(OFFERING_LABEL)}
              </button>
            </>
            ) : null
          }
        />

        <OfferingsSummaryCards
          totalOffered={totalOffered}
          totalPaid={totalPaid}
          totalPending={totalPending}
          count={filteredActive.length}
        />

        {/* Section tabs */}
        <div className={styles.sectionTabs}>
          <button
            className={`${styles.sectionTab} ${activeSection === 'active' ? styles.sectionTabActive : ''}`}
            onClick={() => { setActiveSection('active'); setFilterStatus(''); }}
          >
            Active ({filteredActive.length})
          </button>
          <button
            className={`${styles.sectionTab} ${activeSection === 'cancelled' ? styles.sectionTabActive : ''}`}
            onClick={() => { setActiveSection('cancelled'); setFilterStatus(''); }}
          >
            Cancelled ({filteredCancelled.length})
          </button>
        </div>

        <OfferingsFilters
          years={filterYearOptions}
          categories={categories}
          filterYear={filterYear}
          filterStatus={filterStatus}
          filterCat={filterCat}
          filterMember={filterMember}
          filterFromDate={filterFromDate}
          filterToDate={filterToDate}
          minDate={filterMinDate}
          maxDate={filterMaxDate}
          showStatusFilter={activeSection === 'active'}
          onYearChange={(nextValue) => {
            setFilterYear(nextValue);
          }}
          onStatusChange={setFilterStatus}
          onCatChange={setFilterCat}
          onMemberChange={setFilterMember}
          onFromDateChange={setFilterFromDate}
          onToDateChange={setFilterToDate}
          onClearFilters={clearFilters}
        />

        <OfferingsTable
          pledges={filteredOfferings}
          totalOffered={activeSection === 'active' ? totalOffered : 0}
          totalPaid={activeSection === 'active' ? totalPaid : 0}
          totalPending={activeSection === 'active' ? totalPending : 0}
          onCancelClick={openCancel}
          onEditClick={openEdit}
          onPayClick={(p) => navigate('/pay-offers', { state: { preselectedIds: [p.id] } })}
          onReceiptClick={handleReceiptClick}
          onDeleteClick={openDelete}
          onApproveDeleteRequest={(offering) => handleReviewDeleteRequest(offering, 'approve')}
          onRejectDeleteRequest={(offering) => handleReviewDeleteRequest(offering, 'reject')}
          isDerasarAdmin={isDerasarAdmin}
          canManage={can('offerings', 'manage')}
          activeDerasar={activeDerasar}
          showMemberPayAction={false}
          isCancelledView={activeSection === 'cancelled'}
        />
      </div>

      <OfferingsCancelModal
        pledge={cancelModal}
        reason={cancelReason}
        onReasonChange={setReason}
        onConfirm={handleCancel}
        onClose={closeCancel}
        cancelling={cancelling}
        isDerasarAdmin={isDerasarAdmin}
      />

      <CancelConfirmModal
        entity={ENTITIES.pledge}
        item={deleteModal}
        itemDisplay={deleteModal?.DonationCategory?.name || deleteModal?.donationCategoryId?.name}
        onConfirm={handleDelete}
        onClose={closeDelete}
        cancelling={deleting}
        showReason={false}
        title="Delete Offering"
        confirmLabel="Confirm Delete"
      />

      <OfferingsReceiptModal
        data={receiptModal}
        saving={receiptSaving}
        onConfirm={handleReceiptModalSubmit}
        onClose={() => setReceiptModal(null)}
      />
    </>
  );
};

export default OfferingsPage;
